// Timeline Navigator — React port of kula-create's timeline-navigator.html.
//
// Pedagogy: chronological understanding with cause/effect. Events render as a
// vertical timeline; learner clicks to expand detail. Some events have
// in-line questions that capture brief responses.
//
// Single-row capture per slide: { visited: string[], questionAnswers: {...} }.

import { useState, useCallback, useRef } from 'react';
import { AnimatePresence } from 'framer-motion';
import type { TimelineNavigatorSlideData, Theme, ExperienceConclusion } from '../../slideTypes';
import { slideTypographyStyle } from '../../slideTypes';
import { Reveal } from '../../shared/Reveal';
import { ExperienceClose } from '../../shared/ExperienceClose';
import type { ActivityResponsePayload } from '../../../../../types';
import { TerraTimelineNavigator } from './TerraTimelineNavigator';

interface Props {
  data: TimelineNavigatorSlideData;
  theme: Theme;
  isEditing: boolean;
  onUpdate: (data: TimelineNavigatorSlideData) => void;
  onActivityResponse?: (response: ActivityResponsePayload) => void;
  /** Called exactly once when the learner submits the timeline. */
  onComplete?: () => void;
  /** Last recorded response payload for this slide — restores the submitted view. */
  savedResponse?: Record<string, unknown> | null;
}

export function TimelineNavigatorSlide({
  data,
  theme,
  isEditing,
  onUpdate,
  onActivityResponse,
  onComplete,
  savedResponse,
}: Props) {
  if (isEditing) return <BasicEditor data={data} theme={theme} onUpdate={onUpdate} />;
  // Tier-A bespoke renderer for the "Terra" world (home-set pairing #2).
  if (theme.id === 'terra') {
    return <TerraTimelineNavigator data={data} theme={theme} onActivityResponse={onActivityResponse} onComplete={onComplete} savedResponse={savedResponse} />;
  }
  return (
    <Student
      data={data}
      theme={theme}
      onActivityResponse={onActivityResponse}
      onComplete={onComplete}
      savedResponse={savedResponse}
    />
  );
}

function Student({
  data,
  theme,
  onActivityResponse,
  onComplete,
  savedResponse,
}: {
  data: TimelineNavigatorSlideData;
  theme: Theme;
  onActivityResponse?: (response: ActivityResponsePayload) => void;
  onComplete?: () => void;
  savedResponse?: Record<string, unknown> | null;
}) {
  const accent = theme.colors.accent || theme.colors.primary;
  // The payload ({visited, questionAnswers, reflection}) only fires on submit, so
  // its presence means the timeline finished — restore the submitted view from it.
  const saved: Record<string, unknown> = savedResponse ?? {};
  const restored = Array.isArray(saved.visited);

  // `expanded` is purely visual; `visited` is monotonic (added on expand, never removed).
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [visited, setVisited] = useState<Set<string>>(
    () => new Set(restored ? (saved.visited as string[]) : []),
  );
  const [questionAnswers, setQuestionAnswers] = useState<Record<string, string>>(() =>
    restored && saved.questionAnswers && typeof saved.questionAnswers === 'object'
      ? { ...(saved.questionAnswers as Record<string, string>) }
      : {},
  );
  const [reflection, setReflection] = useState(
    restored && typeof saved.reflection === 'string' ? saved.reflection : '',
  );
  const [submitted, setSubmitted] = useState(restored);
  const completedRef = useRef(restored);

  const allVisited = data.events.length > 0 && data.events.every((e) => visited.has(e.id));
  const hasReflectionPrompt = (data.reflection?.prompt ?? '').trim().length > 0;

  const submit = useCallback(() => {
    setSubmitted(true);
    const responseData: Record<string, unknown> = {
      visited: Array.from(visited),
      questionAnswers,
    };
    // Only include reflection when the author actually set a prompt.
    if (hasReflectionPrompt) responseData.reflection = reflection.trim();
    onActivityResponse?.({
      activityType: 'timeline-navigator',
      mode: 'survey',
      responseData,
    });
    if (!completedRef.current) {
      completedRef.current = true;
      onComplete?.();
    }
  }, [visited, questionAnswers, reflection, hasReflectionPrompt, onActivityResponse, onComplete]);

  // Close: echo the learner's cause-effect answers back, plus the authored "So what?".
  const eventEchoes = data.events
    .filter((e) => e.question)
    .map((e) => ({ label: e.title, value: questionAnswers[e.id] ?? '' }));
  const conclusion: ExperienceConclusion = data.conclusion ?? {
    heading: 'So what',
    body: 'You traced the sequence from start to finish.',
  };

  return (
    <div
      className="h-full w-full overflow-y-auto"
      style={{ background: theme.colors.background, color: theme.colors.text, fontFamily: theme.fonts.body }}
    >
      <div className="mx-auto max-w-[760px] px-6 py-10">
        <header className="text-center mb-6">
          <h1 style={{ ...slideTypographyStyle(theme, 'heading'), color: theme.colors.text, marginBottom: 8 }}>
            {data.title}
          </h1>
          {data.subtitle && <p style={{ color: theme.colors.textMuted, fontSize: '0.95rem' }}>{data.subtitle}</p>}
        </header>

        {data.events.length === 0 ? (
          <div
            className="rounded-lg p-6 text-center text-sm"
            style={{ background: theme.colors.surface, color: theme.colors.textMuted }}
          >
            This timeline has no events yet — there is nothing to explore.
          </div>
        ) : (
          <div className="text-xs text-center mb-6" style={{ color: theme.colors.textMuted }}>
            Visited: {visited.size} of {data.events.length}
          </div>
        )}

        <div className="relative pl-8">
          <div className="absolute left-3 top-2 bottom-2 w-0.5" style={{ background: `${accent}40` }} />
          {data.events.map((event, idx) => {
            const isOpen = expanded.has(event.id);
            return (
              <Reveal
                key={event.id}
                variant="up"
                spring="gentle"
                delay={idx * 0.05}
                className="relative mb-5"
              >
                <div
                  className="absolute -left-[1.6rem] top-2 w-3 h-3 rounded-full"
                  style={{
                    background: event.isMilestone ? accent : theme.colors.surface,
                    border: `2px solid ${accent}`,
                  }}
                />
                <button
                  onClick={() => {
                    setExpanded((prev) => {
                      const next = new Set(prev);
                      if (next.has(event.id)) next.delete(event.id);
                      else next.add(event.id);
                      return next;
                    });
                    // Collapsing must not un-visit — visited only ever grows.
                    setVisited((prev) => (prev.has(event.id) ? prev : new Set(prev).add(event.id)));
                  }}
                  className="w-full text-left rounded-lg p-4 border"
                  style={{
                    background: theme.colors.surface,
                    borderColor: `${accent}30`,
                  }}
                >
                  <p className="text-xs uppercase tracking-wider font-bold mb-1" style={{ color: accent }}>
                    {event.date}
                    {event.isMilestone && ' · milestone'}
                  </p>
                  <h3 className="font-semibold mb-1" style={{ color: theme.colors.text }}>{event.title}</h3>
                  <p className="text-sm" style={{ color: theme.colors.textMuted }}>{event.summary}</p>
                </button>
                <AnimatePresence>
                  {isOpen && (
                    <Reveal
                      variant="up"
                      spring="gentle"
                      className="rounded-xl p-4 mt-2 border-l-[5px] shadow-sm"
                      style={{ background: `${accent}10`, borderColor: accent }}
                    >
                      <p className="text-sm leading-relaxed" style={{ color: theme.colors.text }}>{event.detail}</p>
                      {event.question && (
                        <div className="mt-3">
                          <label className="block text-sm font-medium mb-2" style={{ color: theme.colors.text }}>
                            {event.question}
                          </label>
                          <textarea
                            value={questionAnswers[event.id] ?? ''}
                            onChange={(e) =>
                              setQuestionAnswers((prev) => ({ ...prev, [event.id]: e.target.value }))
                            }
                            disabled={submitted}
                            rows={2}
                            className="w-full px-3 py-2 rounded border text-sm"
                            style={{
                              background: theme.colors.background,
                              borderColor: `${theme.colors.textMuted}33`,
                              color: theme.colors.text,
                            }}
                          />
                        </div>
                      )}
                    </Reveal>
                  )}
                </AnimatePresence>
              </Reveal>
            );
          })}
        </div>

        {/* Finish is gated on visiting every event, NOT on a reflection prompt existing. */}
        <AnimatePresence>
          {allVisited && !submitted && (
            <Reveal
              variant="scale"
              spring="bouncy"
              className="rounded-xl p-5 mt-6 border-2 shadow-sm"
              style={{ background: theme.colors.surface, borderColor: accent }}
            >
              {hasReflectionPrompt && (
                <>
                  <p className="text-xs uppercase tracking-wider font-bold mb-2" style={{ color: accent }}>Reflect</p>
                  <p className="text-sm mb-3" style={{ color: theme.colors.text }}>{data.reflection.prompt}</p>
                  <textarea
                    value={reflection}
                    onChange={(e) => setReflection(e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 rounded border text-sm mb-3"
                    style={{
                      background: theme.colors.background,
                      borderColor: `${theme.colors.textMuted}33`,
                      color: theme.colors.text,
                    }}
                  />
                </>
              )}
              <button
                onClick={submit}
                className="w-full px-6 py-3 rounded-lg font-semibold text-sm"
                style={{ background: accent, color: '#fff' }}
              >
                {hasReflectionPrompt ? 'Submit' : 'Finish'}
              </button>
            </Reveal>
          )}
        </AnimatePresence>

        {/* Authored close — replaces the blank post-submit state with the
            learner's own cause-effect answers echoed back + the "So what?".
            Doubles as the post-submit completion confirmation. */}
        <AnimatePresence>
          {submitted && (
            <Reveal variant="up" className="mt-6">
              <ExperienceClose conclusion={conclusion} theme={theme} echoes={eventEchoes} />
            </Reveal>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

function BasicEditor({
  data,
  theme,
  onUpdate,
}: {
  data: TimelineNavigatorSlideData;
  theme: Theme;
  onUpdate: (data: TimelineNavigatorSlideData) => void;
}) {
  return (
    <div className="h-full w-full overflow-y-auto p-8">
      <div className="mx-auto max-w-[720px] space-y-3">
        <input
          value={data.title}
          onChange={(e) => onUpdate({ ...data, title: e.target.value })}
          placeholder="Title…"
          className="w-full bg-transparent border-b-2 border-transparent hover:border-gray-300 focus:border-blue-500 focus:outline-none"
          style={{ ...slideTypographyStyle(theme, 'heading'), color: theme.colors.text }}
        />
        <input
          value={data.subtitle}
          onChange={(e) => onUpdate({ ...data, subtitle: e.target.value })}
          placeholder="Subtitle"
          className="w-full bg-transparent border-b border-transparent hover:border-gray-300 focus:border-blue-500 focus:outline-none text-sm"
          style={{ color: theme.colors.textMuted }}
        />
        <textarea
          value={data.reflection?.prompt ?? ''}
          onChange={(e) => onUpdate({ ...data, reflection: { prompt: e.target.value } })}
          placeholder="Final reflection prompt"
          rows={2}
          className="w-full px-3 py-2 rounded border text-sm"
          style={{
            background: theme.colors.surface,
            borderColor: `${theme.colors.textMuted}33`,
            color: theme.colors.text,
          }}
        />
        <div className="rounded p-3 text-xs" style={{ background: `${theme.colors.textMuted}11`, color: theme.colors.textMuted }}>
          {data.events.length} event{data.events.length === 1 ? '' : 's'} · {data.events.filter((e) => e.isMilestone).length} milestone(s).
          Structural edits AI-only — regenerate via AI build.
        </div>
      </div>
    </div>
  );
}
